// Pure, dependency-free formatting for irrigation amounts (`wfr_length`).
// Shared by web and mobile — keep platform-agnostic.
//
// WHY 1 DECIMAL FOR METRIC UNITS
//
// The API returns `wfr_length` already rounded to 2 decimals, but it computes
// that number through two different code paths whose rounding order differs:
//
//   FieldManager._structure_wfr_length        round(round(m3 * factor, 2) / area, 2)
//   SensorManager.structure_wfr_for_farming_unit   round(m3 / area, 2)  (converted, then rounded)
//
// Both write the same `historical_data.wfr_length` slot and both are fetched
// concurrently on every field load, so whichever response lands last wins. The
// two results disagree in the last decimal for roughly `0.25 / area_in_dunam`
// of events — ~32% at 0.8 dunam, ~80% at 0.2 dunam — which is why the digit
// visibly changes on small fields. Rendering 1 decimal drops the metric value
// onto a grid the two paths agree on far more often, and matches the precision
// the number is actually trustworthy to.
//
// Inch keeps 2 decimals: 0.1 inch is 2.54 mm, too coarse to be useful.
//
// NOTE: this narrows the window, it does not close it. The durable fix is for
// the two API paths to share one implementation; until then a small number of
// events can still flip, and sub-0.5-dunam fields improve the least.

const INCH_UNITS = ['inch', 'in', 'inches'];

// 2 for imperial length, 1 for mm / cm and anything else.
const irrigationAmountDecimals = unit =>
  INCH_UNITS.includes(
    String(unit || '')
      .trim()
      .toLowerCase()
  )
    ? 2
    : 1;

// The amount as a display string with trailing zeros dropped ("2.7", "3"), or
// null when there is nothing to show. The unit is rendered by the caller.
const formatIrrigationAmount = (value, unit) => {
  const amount = typeof value === 'number' ? value : parseFloat(value);
  if (amount === null || amount === undefined || isNaN(amount)) {
    return null;
  }
  return String(parseFloat(amount.toFixed(irrigationAmountDecimals(unit))));
};

module.exports = {
  irrigationAmountDecimals,
  formatIrrigationAmount,
};
